import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFamilyMembers, createFamilyMember } from '../../api/resources';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../ui/SearchableSelect';
import Modal from '../ui/Modal';
import { orderForLabel } from '../../utils/orderFor';

/**
 * Who the order is for.
 *
 * Off by default, because most orders are for the customer standing there.
 * Ticked, it asks which member of that customer's family — and that answer
 * follows the order everywhere: onto the bill, the job cards, and the
 * measurements, which are then kept under that person's name rather than the
 * customer's.
 *
 * One order is for one person. A father ordering for himself and his son
 * books two orders, which is also how the shop hands the clothes over.
 */
export default function FamilyMemberField({ form, set, label: L }) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [adding, setAdding] = useState(null); // the name typed when nothing matched
  const [relation, setRelation] = useState('');
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ['wizard-family', form.customerId],
    queryFn: () => fetchFamilyMembers(form.customerId),
    enabled: !!form.customerId,
  });
  const members = data?.familyMembers || [];
  const options = members.map((m) => ({
    id: m.id,
    label: m.name,
    sub: m.relation || m.mobile || '',
    raw: m,
  }));

  const choose = (m) => set({
    familyMemberId: String(m.id),
    familyMemberName: m.name,
    familyMemberRelation: m.relation || '',
  });

  const create = async () => {
    const name = String(adding || '').trim();
    if (!name) return;
    setBusy(true);
    try {
      const res = await createFamilyMember(form.customerId, { name, relation: relation.trim() || null });
      const made = res.familyMember || res;
      qc.invalidateQueries({ queryKey: ['wizard-family', form.customerId] });
      choose(made);
      success(`${made.name} added to the family.`);
      setAdding(null);
      setRelation('');
    } catch (e) {
      error(e.response?.data?.message || 'Could not add them.');
    } finally {
      setBusy(false);
    }
  };

  const clear = {
    forFamily: false, familyMemberId: '', familyMemberName: '', familyMemberRelation: '',
  };

  return (
    <div className="col-span-2 md:col-span-6 xl:col-span-12">
      <label className="flex w-fit items-center gap-2 text-xs font-semibold text-content">
        <input
          type="checkbox"
          className="h-4 w-4 accent-brand-600"
          checked={!!form.forFamily}
          disabled={!form.customerId}
          onChange={(e) => set(e.target.checked ? { forFamily: true } : clear)}
        />
        This order is for a family member
        {!form.customerId && (
          <span className="font-normal text-content-subtle">— choose a customer first</span>
        )}
      </label>

      {form.forFamily && (
        <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-2 md:grid-cols-6 xl:grid-cols-12">
          <div className="col-span-2 md:col-span-4 xl:col-span-5">
            <L>Clothes are for <span className="text-danger-600">*</span></L>
            <SearchableSelect
              options={options}
              value={form.familyMemberId}
              onSelect={(o) => choose(o.raw || o)}
              selectedLabel={form.familyMemberName
                ? form.familyMemberName + (form.familyMemberRelation ? ' · ' + form.familyMemberRelation : '')
                : ''}
              placeholder="Type a name — Enter adds them to the family"
              searchable
              allowNew
              onNew={(text) => { setAdding(text); setRelation(''); }}
            />
          </div>
          {form.familyMemberName && (
            <p className="col-span-2 self-end pb-2 text-2xs text-content-muted md:col-span-6 xl:col-span-7">
              The bill and the job cards will read{' '}
              <b className="text-content">
                {orderForLabel({ name: form.customerName }, {
                  name: form.familyMemberName, relation: form.familyMemberRelation,
                })}
              </b>
              . Measurements are saved under {form.familyMemberName}.
              {!form.familyMemberRelation && ' Add a relation and it reads that instead.'}
            </p>
          )}
        </div>
      )}

      <Modal
        open={!!adding}
        onClose={() => { setAdding(null); setRelation(''); }}
        title="Add to the family"
        size="sm"
        footer={(
          <>
            <button className="btn-outline" onClick={() => { setAdding(null); setRelation(''); }}>
              Cancel
            </button>
            <button className="btn-primary" disabled={busy || !String(adding || '').trim()} onClick={create}>
              {busy ? 'Adding…' : 'Add'}
            </button>
          </>
        )}
      >
        <label className="block">
          <L>Name</L>
          <input className="input" autoFocus value={adding || ''} onChange={(e) => setAdding(e.target.value)} />
        </label>
        <label className="mt-3 block">
          <L>Relation</L>
          <input
            className="input"
            placeholder="son, wife, father…"
            value={relation}
            onChange={(e) => setRelation(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
          />
        </label>
      </Modal>
    </div>
  );
}
